import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

function isImageFile(file: File): boolean {
  return IMAGE_MIME_TYPES.includes(file.type);
}

async function insertUploadedImage(
  view: EditorView,
  file: File,
  onUpload: (file: File) => Promise<string>
): Promise<void> {
  try {
    const url = await onUpload(file);
    const { schema } = view.state;
    const imageNode = schema.nodes.image?.create({ src: url, alt: file.name });
    if (imageNode) {
      const transaction = view.state.tr.replaceSelectionWith(imageNode);
      view.dispatch(transaction);
    }
  } catch {
    // Error handling done by the onUpload callback
  }
}

export interface ImageUploadStorage {
  uploadFn: ((file: File) => Promise<string>) | null;
}

/**
 * Tiptap Extension for image paste/drop upload.
 * Set `editor.storage.imageUpload.uploadFn` to enable upload handling.
 * When `uploadFn` is null, paste/drop events pass through normally.
 */
export const ImageUploadExtension = Extension.create<
  Record<string, never>,
  ImageUploadStorage
>({
  name: 'imageUpload',

  addStorage() {
    return {
      uploadFn: null,
    };
  },

  addProseMirrorPlugins() {
    const { storage } = this;

    return [
      new Plugin({
        key: new PluginKey('imageUpload'),
        props: {
          handlePaste(view, event) {
            if (!storage.uploadFn) return false;
            const items = event.clipboardData?.items;
            if (!items) return false;

            for (const item of Array.from(items)) {
              const file = item.getAsFile();
              if (file && isImageFile(file)) {
                event.preventDefault();
                insertUploadedImage(view, file, storage.uploadFn);
                return true;
              }
            }
            return false;
          },
          handleDrop(view, event) {
            if (!storage.uploadFn) return false;
            const files = event.dataTransfer?.files;
            if (!files?.length) return false;

            const imageFile = Array.from(files).find(isImageFile);
            if (imageFile) {
              event.preventDefault();
              insertUploadedImage(view, imageFile, storage.uploadFn);
              return true;
            }
            return false;
          },
        },
      }),
    ];
  },
});
